import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Timer, Download } from "lucide-react";
import { useLang } from "@/context/LangContext";

/**
 * Download interstitial: shows an ad break with a 5-second countdown, then
 * closes automatically and starts the real download.
 */
export default function DownloadInterstitial({ open, onClose }) {
  const { t } = useLang();
  const [left, setLeft] = useState(5);
  const adRoot = useRef(null);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    setLeft(5);
    const iv = setInterval(() => setLeft((s) => (s > 0 ? s - 1 : 0)), 1000);
    const timeout = window.setTimeout(() => onCloseRef.current(), 5500);
    return () => {
      clearInterval(iv);
      window.clearTimeout(timeout);
    };
  }, [open]);

  useEffect(() => {
    if (!open || !adRoot.current) return;
    const root = adRoot.current;
    root.replaceChildren();
    const script = document.createElement("script");
    script.async = true;
    script.dataset.zone = "11717225";
    script.src = "https://al5sm.com/tag.min.js";
    root.appendChild(script);
    return () => root.replaceChildren();
  }, [open]);

  if (!open) return null;
  const ready = left === 0;

  return (
    <motion.div
      data-testid="download-interstitial"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-void/95 backdrop-blur-md p-6"
      role="dialog"
      aria-modal="true"
      aria-label={t("ad.label")}
    >
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-surface/80 p-8 text-center shadow-2xl">
        <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-muted-foreground/60">
          {t("ad.label")}
        </span>

        <div className="mx-auto my-6 flex h-24 w-24 items-center justify-center rounded-full border-2 border-neon/30 bg-neon/5">
          {ready ? (
            <Download className="h-10 w-10 text-neon" />
          ) : (
            <span className="font-display text-4xl font-black text-neon" data-testid="interstitial-countdown">
              {left}
            </span>
          )}
        </div>

        <p className="mb-6 text-sm text-muted-foreground">
          {ready ? t("ad.ready") : t("ad.wait")}
        </p>

        <div
          ref={adRoot}
          data-testid="interstitial-ad-slot"
          className="min-h-0"
          aria-label={t("ad.label")}
        />

        <div
          data-testid="interstitial-auto-status"
          aria-live="polite"
          className={`inline-flex items-center gap-2 rounded-full px-8 py-3 font-bold transition-colors duration-200 ${
            ready
              ? "btn-neon"
              : "border border-white/10 text-muted-foreground/40"
          }`}
        >
          {ready ? <Download className="h-4 w-4" /> : <Timer className="h-4 w-4 animate-pulse" />}
          {ready ? t("ad.ready") : `${left} ${t("dl.secs")}`}
        </div>
      </div>
    </motion.div>
  );
}
